import React, {useCallback, useEffect, useState} from "react"
import {useTranslation} from "react-i18next"

import {DomainUser, TenantUser} from "src/_generated/typed-document-nodes"

import {Tenant} from "../utils/@types/TenantInterface"
import Loader from "../utils/Loader"
import {initialDomainUser} from "../utils/Queries/domainUsers"
import {initialTenantUser} from "../utils/Queries/tenantUsers"
import ErrorContext, {ErrorContextType} from "./ErrorContext"

function DocoonProvider({children, cache}: any) {
    const {t} = useTranslation("errors")

    const [tenant, setTenant] = useState<Tenant>({
        id: "",
        domainId: "",
        domainName: "",
        parentDomainId: "",
        parentDomainName: "",
        name: "",
        phone: "",
        email: "",
        address: {
            name: "",
            additionalInfo: "",
            additionalGeographicInfo: "",
            street: "",
            postBox: "",
            zipCode: "",
            city: "",
            country: ""
        },
        omsServiceAccount: null,
        authorizedProductIds: [],
        isEnabled: true
    })

    const [tenantUser, setTenantUser] = useState<TenantUser>(initialTenantUser)
    const [domainUser, setDomainUser] = useState<DomainUser>(initialDomainUser)

    const [globalLoading, setGlobalLoading] = useState(false)

    // Gestion centralisée des messages d'erreurs
    const errorMessagesMap: Record<string, string> = {
        "Action is not allowed": t("errors:NotAllowed"),
        EdcOmsEmpty: t("errors:EdcOmsEmpty"),
        TypeRequired: t("errors:TypeRequired"),
        "Received status code 401": "unauthorized",
        token: "unauthorized",
        role: "unauthorized",
        erreur: "unauthorized",
        FunctionRequired: t("errors:FunctionRequired"),
        "comming soon": t("errors:CommingSoon")
    }

    const handleUnauthorized = useCallback(() => {
        const columnView = localStorage.getItem("columnView") || ""
        const idToken = localStorage.getItem("idToken") || ""
        const accessToken = localStorage.getItem("accessToken") || ""
        localStorage.clear()
        localStorage.setItem("columnView", columnView)
        localStorage.setItem("idToken", idToken)
        localStorage.setItem("accessToken", accessToken)
        window.location.href = "/"
    }, [])

    const setIsError = useCallback(
        (message: string) => {
            const foundMessage = Object.keys(errorMessagesMap).find((key) => message.includes(key))

            if (foundMessage) {
                const errorMessage = errorMessagesMap[foundMessage]
                if (errorMessage === "unauthorized") {
                    handleUnauthorized()
                } else {
                    setValue((prev) => ({
                        ...prev,
                        isError: true,
                        message: errorMessage
                    }))
                }
            } else {
                // Optional fallback for other general errors
                setValue({
                    ...value,
                    isError: true,
                    message: t("errors:GeneralError")
                })
            }

            setTimeout(() => setValue((prev) => ({...prev, isError: false})), 4000)
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, handleUnauthorized, errorMessagesMap]
    )

    const [value, setValue] = useState<ErrorContextType>({
        isError: false,
        message: "",
        generateError: setIsError,
        cache,
        globalLoading,
        setGlobalLoading,
        currentTenant: tenant,
        setCurrentTenant: setTenant,
        currentTenantUser: tenantUser,
        currentDomainUser: domainUser,
        setCurrentTenantUser: setTenantUser,
        setCurrentDomainUser: setDomainUser
    })

    useEffect(
        () => {
            const storageUser = localStorage.getItem("tenantUser")
                ? JSON.parse(localStorage.getItem("tenantUser"))
                : null
            if (
                storageUser?.email &&
                storageUser.email.length > 0 &&
                !(tenantUser.email && tenantUser.email.length > 0)
            ) {
                setValue({...value, currentTenantUser: storageUser})
                localStorage.setItem("tenantUser", JSON.stringify(storageUser))
            } else {
                setValue({...value, currentTenantUser: tenantUser})
                localStorage.setItem("tenantUser", JSON.stringify(tenantUser))
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tenantUser]
    )
    useEffect(
        () => {
            setValue({...value, currentTenant: tenant})
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tenant]
    )

    return (
        <ErrorContext.Provider value={value}>
            <Loader isLoading={globalLoading} style={{zIndex: 999999}} />
            {!globalLoading && children}
        </ErrorContext.Provider>
    )
}

export default DocoonProvider
